// src/api-schemas.ts
import { z as z3 } from "zod";

// src/providers.ts
import { z as z2 } from "zod";

// src/rich-text.ts
import { z } from "zod";
var MarkSchema = z.union([
  z.object({ type: z.literal("bold") }),
  z.object({ type: z.literal("italic") })
]);
var InlineSchema = z.union([
  z.object({
    type: z.literal("text"),
    text: z.string().min(1),
    marks: z.array(MarkSchema).optional()
  }),
  z.object({ type: z.literal("hardBreak") })
]);
var ParagraphSchema = z.object({
  type: z.literal("paragraph"),
  content: z.array(InlineSchema).optional()
});
var HeadingSchema = z.object({
  type: z.literal("heading"),
  attrs: z.object({ level: z.literal(3) }),
  content: z.array(InlineSchema).optional()
});
var ListItemSchema = z.lazy(
  () => z.object({
    type: z.literal("listItem"),
    content: z.array(z.union([ParagraphSchema, BulletListSchema, OrderedListSchema])).min(1)
  })
);
var BulletListSchema = z.object({
  type: z.literal("bulletList"),
  content: z.array(ListItemSchema).min(1)
});
var OrderedListSchema = z.object({
  type: z.literal("orderedList"),
  attrs: z.object({ start: z.number().int().min(0).optional() }).optional(),
  content: z.array(ListItemSchema).min(1)
});
var RichTextDocSchema = z.object({
  type: z.literal("doc"),
  content: z.array(z.union([ParagraphSchema, HeadingSchema, BulletListSchema, OrderedListSchema]))
});
function isRichTextEmpty(node) {
  if (node.type === "text") return node.text.trim() === "";
  if (node.type === "hardBreak") return true;
  return (node.content ?? []).every(isRichTextEmpty);
}
function richTextFromPlain(text) {
  return { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text }] }] };
}

// src/providers.ts
var ProviderNameSchema = z2.enum(["pen", "pcrs", "allied", "ascent"]);
var TypedFieldSpecSchema = z2.object({
  key: z2.string(),
  label: z2.string(),
  type: z2.enum(["text", "integer", "select", "boolean"]),
  required: z2.boolean(),
  helpText: z2.string().optional(),
  options: z2.array(z2.object({ value: z2.string(), label: z2.string() })).optional(),
  writtenByService: z2.boolean().optional()
});
var PEN_ACCOUNT_FIELDS = [
  {
    key: "pen_dealer_id",
    label: "PEN dealer ID",
    type: "integer",
    required: false,
    writtenByService: true,
    helpText: "Set by registerAccount() (PEN SetDealer)."
  }
];
var PCRS_ACCOUNT_FIELDS = [
  {
    key: "pcrs_dealer_id",
    label: "PCRS dealer ID",
    type: "text",
    required: true
  },
  {
    key: "subprovider",
    label: "Sub-provider",
    type: "select",
    required: true,
    options: [
      { value: "ascent", label: "Ascent" },
      { value: "trucksuite", label: "TruckSuite" }
    ],
    helpText: 'Selects which PCRS endpoint + credentials are used. Also surfaced as a display suffix in admin provider lists ("PCRS (ascent)" / "PCRS (trucksuite)") via `formatProviderLabel`.'
  }
];
var ALLIED_ACCOUNT_FIELDS = [
  {
    key: "branch_code",
    label: "Branch code",
    type: "text",
    required: true,
    helpText: "Appended to every Allied API call for this account."
  }
];
var ASCENT_ACCOUNT_FIELDS = [
  {
    key: "ascent_dealer_number",
    label: "Ascent dealer number",
    type: "text",
    required: true,
    helpText: 'Pre-issued by Ascent (e.g. "ASCE-DLR-001"). Sent as dealerNumber on every GetEligibleCoverages and CreateContract call.'
  }
];
var PEN_PRODUCT_FIELDS = [
  {
    key: "pen_product_id",
    label: "PEN product ID",
    type: "integer",
    required: true,
    helpText: "PEN's catalog identifier for the product."
  },
  {
    key: "pen_provider_id",
    label: "PEN provider ID",
    type: "integer",
    required: false,
    writtenByService: true,
    helpText: "PEN's underwriter identifier \u2014 returned by registerProduct() and used at issuance time."
  },
  {
    key: "pen_provider_dealer_code",
    label: "PEN provider dealer code",
    type: "text",
    required: true,
    helpText: "Per-dealer-per-product code supplied by the admin during product setup. Sent to PEN's SetDealerProduct call."
  },
  {
    key: "pen_form_id",
    label: "PEN form ID",
    type: "integer",
    required: true,
    helpText: "PEN's warranty form identifier \u2014 pins this product to one specific form. `pen_product_id` is a catalog umbrella that fans out across many forms; this narrows it to one. Read off `coverage.formId` in a test-rate response."
  },
  {
    key: "pen_coverage_class",
    label: "PEN coverage class",
    type: "text",
    required: true,
    helpText: "Class / variant suffix on the coverage name (e.g. 'PLATINUM', 'GOLD', 'SENTRY CARE'). One PEN form often hosts both GOLD and PLATINUM under the same `formId`, so this disambiguates. Match is on the trailing segment of `coverageName` after the last ' - '."
  }
];
var PCRS_PRODUCT_FIELDS = [
  {
    key: "pcmi_product_id",
    label: "PCMI product ID",
    type: "text",
    required: true,
    helpText: "Match key for PCRS rating responses. Must equal the `rate.code` PCRS returns. Also sent as `coverage.planCode` during contract issuance."
  }
];
var ALLIED_PRODUCT_FIELDS = [
  {
    key: "allied_plan_name",
    label: "Allied plan name",
    type: "text",
    required: true,
    helpText: "Exact PlanName from Allied. Case-sensitive. Matched against `Quotes.MBP.Details.PlanName` to filter rated tiers."
  }
];
var ASCENT_PRODUCT_FIELDS = [
  {
    key: "ascent_coverage_code",
    label: "Ascent coverage code",
    type: "text",
    required: true,
    helpText: "Ascent's catalog identifier (e.g. 'PNC - VSC - GOLD'). Matched against GetEligibleCoverages response data[].coverageCode at rate time. Case-sensitive."
  }
];
var RateInputSpecSchema = z2.object({
  buyerAddress: z2.boolean(),
  fields: z2.array(TypedFieldSpecSchema),
  attestation: z2.string().optional()
});
var NO_RATE_INPUTS = { buyerAddress: false, fields: [] };
var ALLIED_RATE_INPUTS = {
  buyerAddress: true,
  fields: [
    {
      key: "allied_used_commercially",
      label: "Will the vehicle be used commercially?",
      type: "boolean",
      required: true
    },
    {
      key: "allied_salvaged_title",
      label: "Does the vehicle have a branded or salvage title?",
      type: "boolean",
      required: true
    }
  ],
  attestation: "I attest that the mileage entered is accurate and correct."
};
var providerRegistry = {
  pen: {
    label: "PEN",
    pricingMode: "markup",
    accountFields: PEN_ACCOUNT_FIELDS,
    productFields: PEN_PRODUCT_FIELDS,
    rateInputs: NO_RATE_INPUTS,
    supportsOutreach: true,
    sharesContractsAcrossItems: true
  },
  pcrs: {
    label: "PCRS",
    pricingMode: "markup",
    accountFields: PCRS_ACCOUNT_FIELDS,
    productFields: PCRS_PRODUCT_FIELDS,
    rateInputs: NO_RATE_INPUTS,
    supportsOutreach: true,
    sharesContractsAcrossItems: false
  },
  allied: {
    label: "Allied",
    pricingMode: "all_in",
    accountFields: ALLIED_ACCOUNT_FIELDS,
    productFields: ALLIED_PRODUCT_FIELDS,
    rateInputs: ALLIED_RATE_INPUTS,
    supportsOutreach: false,
    sharesContractsAcrossItems: false
  },
  ascent: {
    label: "Ascent",
    pricingMode: "markup",
    accountFields: ASCENT_ACCOUNT_FIELDS,
    productFields: ASCENT_PRODUCT_FIELDS,
    rateInputs: NO_RATE_INPUTS,
    supportsOutreach: true,
    sharesContractsAcrossItems: false
  }
};
for (const [provider, entry] of Object.entries(providerRegistry)) {
  const needsInput = entry.rateInputs.buyerAddress || entry.rateInputs.fields.length > 0;
  if (entry.supportsOutreach && needsInput) {
    throw new Error(`Provider ${provider} declares rateInputs, so it cannot support outreach`);
  }
}
var OUTREACH_PROVIDERS = Object.keys(providerRegistry).filter((provider) => providerRegistry[provider].supportsOutreach);
var RateInputRequirementsSchema = z2.object({
  buyer_address: z2.boolean(),
  fields: z2.array(TypedFieldSpecSchema),
  attestations: z2.array(z2.string())
});
function mergeRateInputs(providers) {
  const fields = [];
  const seen = /* @__PURE__ */ new Set();
  const attestations = [];
  let buyerAddress = false;
  for (const provider of providers) {
    const spec = providerRegistry[provider].rateInputs;
    buyerAddress = buyerAddress || spec.buyerAddress;
    for (const field of spec.fields) {
      if (seen.has(field.key)) continue;
      seen.add(field.key);
      fields.push(field);
    }
    if (spec.attestation !== void 0 && !attestations.includes(spec.attestation)) {
      attestations.push(spec.attestation);
    }
  }
  return { buyer_address: buyerAddress, fields, attestations };
}
function zodFieldSchema(spec) {
  switch (spec.type) {
    case "integer":
      return z2.number().int();
    case "text":
      return z2.string().min(1);
    case "boolean":
      return z2.boolean();
    case "select": {
      const values = (spec.options ?? []).map((o) => o.value);
      if (values.length === 0) {
        throw new Error(`Provider field ${spec.key} is type 'select' but has no options`);
      }
      return z2.enum(values);
    }
  }
}
function typedFieldsSchema(fields) {
  const shape = {};
  for (const spec of fields) {
    let schema = zodFieldSchema(spec);
    const optional = !spec.required || spec.writtenByService === true;
    if (optional) {
      schema = schema.optional();
    }
    shape[spec.key] = schema;
  }
  return z2.object(shape).passthrough();
}
function formatProviderLabel(provider, providerData) {
  const base = providerRegistry[provider].label;
  if (provider === "pcrs") {
    const sub = providerData.subprovider;
    if (typeof sub === "string" && sub.length > 0) {
      const field = providerRegistry[provider].accountFields.find((f) => f.key === "subprovider");
      const display = field ? formatProviderFieldValue(field, sub) : sub;
      return `${base} (${display})`;
    }
  }
  return base;
}
function formatProviderFieldValue(field, value) {
  if (field.type === "select") {
    const match = field.options?.find((o) => o.value === value);
    if (match) return match.label;
  }
  if (field.type === "boolean") return value === true ? "Yes" : "No";
  return String(value);
}
function accountProviderDataSchemaFor(provider) {
  return typedFieldsSchema(providerRegistry[provider].accountFields);
}
function productDataSchemaFor(provider) {
  return typedFieldsSchema(providerRegistry[provider].productFields);
}
var AccountProviderSchema = z2.object({
  id: z2.string().uuid(),
  account_id: z2.string().uuid(),
  provider: ProviderNameSchema,
  enabled: z2.boolean(),
  provider_data: z2.record(z2.unknown()),
  created_at: z2.string(),
  updated_at: z2.string()
});
var AccountProviderListResponseSchema = z2.object({
  account_providers: z2.array(AccountProviderSchema)
});
var CreateAccountProviderRequestSchema = z2.object({
  provider: ProviderNameSchema,
  enabled: z2.boolean().optional(),
  provider_data: z2.record(z2.unknown())
});
var UpdateAccountProviderRequestSchema = z2.object({
  enabled: z2.boolean().optional(),
  provider_data: z2.record(z2.unknown()).optional()
});
var ProvidersDiscoveryResponseSchema = z2.object({
  providers: z2.array(
    z2.object({
      provider: ProviderNameSchema,
      label: z2.string(),
      accountFields: z2.array(TypedFieldSpecSchema),
      productFields: z2.array(TypedFieldSpecSchema),
      rateInputs: RateInputSpecSchema
    })
  )
});
var ProductDescriptionInputSchema = RichTextDocSchema.refine((d) => !isRichTextEmpty(d), {
  message: "Description is required."
});
var ProductSchema = z2.object({
  id: z2.string().uuid(),
  account_provider_id: z2.string().uuid(),
  account_id: z2.string().uuid(),
  provider: ProviderNameSchema,
  name: z2.string(),
  description: RichTextDocSchema,
  enabled: z2.boolean(),
  product_type: z2.string(),
  bundle_types: z2.array(z2.string()),
  markup_pct_override: z2.number().nullable(),
  fixed_markup_override: z2.number().nullable(),
  sellmore_fee_override: z2.number().nullable(),
  dealer_cost: z2.number().nullable(),
  is_taxable: z2.boolean(),
  tax_rate_override: z2.number().nullable(),
  provider_data: z2.record(z2.unknown()),
  created_at: z2.string(),
  updated_at: z2.string()
});
var ProductListResponseSchema = z2.object({
  products: z2.array(ProductSchema)
});
var CreateProductRequestSchema = z2.object({
  account_provider_id: z2.string().uuid(),
  name: z2.string().min(1),
  description: ProductDescriptionInputSchema,
  enabled: z2.boolean().optional(),
  product_type: z2.string().min(1),
  bundle_types: z2.array(z2.string()).optional(),
  markup_pct_override: z2.number().nonnegative().nullable().optional(),
  fixed_markup_override: z2.number().nonnegative().nullable().optional(),
  sellmore_fee_override: z2.number().nonnegative().nullable().optional(),
  dealer_cost: z2.number().nonnegative().nullable().optional(),
  is_taxable: z2.boolean().optional(),
  tax_rate_override: z2.number().nonnegative().max(1).nullable().optional(),
  provider_data: z2.record(z2.unknown())
});
var UpdateProductRequestSchema = z2.object({
  name: z2.string().min(1).optional(),
  description: ProductDescriptionInputSchema.optional(),
  enabled: z2.boolean().optional(),
  product_type: z2.string().min(1).optional(),
  bundle_types: z2.array(z2.string()).optional(),
  markup_pct_override: z2.number().nonnegative().nullable().optional(),
  fixed_markup_override: z2.number().nonnegative().nullable().optional(),
  sellmore_fee_override: z2.number().nonnegative().nullable().optional(),
  dealer_cost: z2.number().nonnegative().nullable().optional(),
  is_taxable: z2.boolean().optional(),
  tax_rate_override: z2.number().nonnegative().max(1).nullable().optional(),
  provider_data: z2.record(z2.unknown()).optional()
});
var CopyProductsRequestSchema = z2.object({
  target_account_id: z2.string().uuid(),
  source_account_id: z2.string().uuid(),
  product_ids: z2.array(z2.string().uuid()).optional()
});
var CopyProductsResponseSchema = z2.object({
  copied: z2.array(ProductSchema),
  skipped: z2.array(
    z2.object({
      source_product_id: z2.string().uuid(),
      reason: z2.literal("TARGET_NOT_CONFIGURED_FOR_PROVIDER"),
      provider: ProviderNameSchema
    })
  )
});

// src/domain-types.ts
var MAX_OUTREACH_STEPS = 3;
var PRODUCT_TYPES = [
  "Accessories",
  "Anti-Theft Device",
  "Appearance Protection",
  "Battery",
  "Bundle",
  "Dent",
  "Gap Insurance",
  "GPS",
  "Identity Theft",
  "Interior Protection",
  "Key Replacement",
  "Lease Coverage",
  "Membership Program",
  "Paint Sealant",
  "Pre-paid Maintenance",
  "PS VSC ATV",
  "Rust Protection",
  "Theft Registration",
  "Tire & Wheel",
  "Vehicle Replacement",
  "VSC",
  "Windshield Coverage",
  "PS Accessories",
  "PS Anti-Theft Device",
  "PS Appearance Protection",
  "PS Battery",
  "PS Bundle",
  "PS Dent",
  "PS Gap Insurance",
  "PS Identity Theft",
  "PS Key Replacement",
  "PS Membership Program",
  "PS Paint Sealant",
  "PS Pre-paid Maintenance",
  "PS Rust Protection",
  "PS Theft Registration",
  "PS Tire & Wheel",
  "PS VSC",
  "PS VSC Snowmobile",
  "PS VSC Trailer",
  "PS Wear Care"
];
var BUNDLE_PRODUCT_TYPES = ["Bundle", "PS Bundle"];

// src/pricing.ts
function roundCents(n) {
  return Math.round(n * 100) / 100;
}
var PricingService = class {
  static calculateSalePrice(inputs, paymentType) {
    const feePct = paymentType === "finance" ? inputs.financingFeePct : inputs.paidInFullFeePct;
    return inputs.basePrice * (1 + inputs.markupPct + feePct) + inputs.fixedMarkup + inputs.sellmoreFee;
  }
  static resolveTaxRate(inputs) {
    if (!inputs.isTaxable) return 0;
    return inputs.taxRateOverride ?? inputs.accountDefaultTaxRate;
  }
  static calculateTaxes(salePrice, taxRate) {
    return salePrice * taxRate;
  }
  static calculateTotal(salePrice, taxes, surcharges) {
    const surchargeSum = surcharges.filter((s) => s.selected).reduce((sum, s) => sum + s.basePrice, 0);
    return salePrice + taxes + surchargeSum;
  }
  static calculateMargin(salePrice, dealerCost) {
    if (dealerCost == null) return null;
    return salePrice - dealerCost;
  }
};
function resolvePricingInputs(account, product, basePrice, pricingMode) {
  if (pricingMode === "all_in") {
    return {
      basePrice,
      markupPct: 0,
      fixedMarkup: 0,
      sellmoreFee: 0,
      financingFeePct: 0,
      paidInFullFeePct: 0,
      dealerCost: product.dealerCost,
      taxRate: 0
    };
  }
  return {
    basePrice,
    markupPct: product.markupPctOverride ?? account.defaultMarkupPct,
    fixedMarkup: product.fixedMarkupOverride ?? account.defaultFixedMarkup,
    sellmoreFee: product.sellmoreFeeOverride ?? account.defaultSellmoreFee,
    financingFeePct: account.financingFeePct,
    paidInFullFeePct: account.paidInFullFeePct,
    dealerCost: product.dealerCost,
    taxRate: PricingService.resolveTaxRate({
      isTaxable: product.isTaxable,
      taxRateOverride: product.taxRateOverride,
      accountDefaultTaxRate: account.defaultTaxRate
    })
  };
}

// src/financing.ts
var DEFAULT_FINANCE_LIMITS = {
  minMonths: 6,
  maxMonths: 36,
  minDownPaymentPct: 0.1
};
var MAX_FINANCE_TERM_PCT = 0.5;
var MAX_FINANCE_MONTHS = 36;
var FINANCE_STEP_MONTHS = 6;
function maxFinanceMonths(termMonths, limits) {
  return Math.min(Math.floor(termMonths * MAX_FINANCE_TERM_PCT), limits.maxMonths);
}
function financeDurationOptions(termMonths, limits) {
  const max = maxFinanceMonths(termMonths, limits);
  const out = [];
  for (let months = limits.minMonths; months <= max; months += FINANCE_STEP_MONTHS) {
    out.push(months);
  }
  return out;
}
function canFinance(termMonths, limits) {
  return maxFinanceMonths(termMonths, limits) >= limits.minMonths;
}
function minDownPayment(wholesale, limits) {
  return roundCents(wholesale * limits.minDownPaymentPct);
}
var pctFmt = new Intl.NumberFormat(void 0, { style: "percent", maximumFractionDigits: 3 });
function minDownPaymentLabel(limits) {
  return pctFmt.format(limits.minDownPaymentPct);
}
function clampFinanceDuration(termMonths, preferred, limits) {
  const options = financeDurationOptions(termMonths, limits);
  if (options.length === 0) return null;
  const fitting = options.filter((m) => m <= preferred);
  return fitting.length > 0 ? fitting[fitting.length - 1] : options[0];
}

// src/phone.ts
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js/min";
var DEFAULT_COUNTRY = "US";
function formatPhoneInput(input) {
  return new AsYouType(DEFAULT_COUNTRY).input(input);
}
function phoneToE164(input) {
  const parsed = parsePhoneNumberFromString(input, DEFAULT_COUNTRY);
  return parsed && parsed.isPossible() ? parsed.number : null;
}
function formatPhoneDisplay(value) {
  const parsed = parsePhoneNumberFromString(value, DEFAULT_COUNTRY);
  return parsed ? parsed.formatNational() : value;
}

// src/api-schemas.ts
var NormalizedPhoneSchema = z3.string().min(1).transform((value, ctx) => {
  const e164 = phoneToE164(value);
  if (e164 === null) {
    ctx.addIssue({ code: z3.ZodIssueCode.custom, message: "Not a valid phone number." });
    return z3.NEVER;
  }
  return e164;
});
var RateBuyerAddressSchema = z3.object({
  street1: z3.string().min(1),
  city: z3.string().min(1),
  state: z3.string().min(1),
  postal_code: z3.string().min(1)
});
var paginationQueryFields = {
  limit: z3.coerce.number().int().positive().max(100).optional().default(25),
  offset: z3.coerce.number().int().nonnegative().optional().default(0)
};
var sortOrderField = z3.enum(["asc", "desc"]).optional().default("desc");
var paginatedListFields = {
  total: z3.number().int().nonnegative(),
  limit: z3.number().int().positive(),
  offset: z3.number().int().nonnegative()
};
var SurchargeSchema = z3.object({
  code: z3.string(),
  description: z3.string(),
  basePrice: z3.number().nonnegative(),
  optional: z3.boolean(),
  selected: z3.boolean()
});
var SurchargeWireSchema = z3.object({
  code: z3.string(),
  description: z3.string(),
  base_price: z3.number().nonnegative(),
  optional: z3.boolean(),
  selected: z3.boolean()
});
var VehicleInputSchema = z3.object({
  vin: z3.string().length(17),
  mileage: z3.number().int().nonnegative(),
  year: z3.number().int().min(1900).max(2100).optional(),
  make: z3.string().min(1).optional(),
  model: z3.string().min(1).optional(),
  /** ISO date string (YYYY-MM-DD). Backend defaults to today when omitted. */
  in_service_date: z3.string().optional()
});
var RatedProductWireSchema = z3.object({
  product_id: z3.string().uuid(),
  product_name: z3.string(),
  product_description: RichTextDocSchema,
  coverage_name: z3.string(),
  product_type: z3.string(),
  term_months: z3.number().int().nonnegative(),
  term_miles: z3.number().int().nonnegative(),
  deductible_amount: z3.number().nullable(),
  deductible_type: z3.string().nullable(),
  base_price: z3.number().nonnegative(),
  surcharges: z3.array(SurchargeWireSchema),
  quote_item_id: z3.string().uuid(),
  sale_price_finance: z3.number().nonnegative(),
  sale_price_pif: z3.number().nonnegative(),
  tax_rate: z3.number().nonnegative(),
  provider_data: z3.record(z3.unknown())
});
var TestRatedProductWireSchema = RatedProductWireSchema.omit({
  quote_item_id: true,
  sale_price_finance: true,
  sale_price_pif: true,
  tax_rate: true
});
var FlowTypeSchema = z3.enum(["marketplace", "renewal", "reconsideration", "test"]);
var RateInputsSchema = z3.object({
  buyer_address: RateBuyerAddressSchema.optional(),
  fields: z3.record(z3.union([z3.string(), z3.number(), z3.boolean()])).default({})
});
var RateRequestSchema = z3.object({
  flow_type: FlowTypeSchema,
  vehicle: VehicleInputSchema,
  // Required for recon/renewal; forbidden for marketplace. Server stamps
  // responded_at + quote_id on the outreach at quote-mint.
  outreach_id: z3.string().uuid().optional(),
  // Outreach omits these; the server fills `buyer_address` from the seeded customer.
  rate_inputs: RateInputsSchema.optional()
});
var RatingErrorCodeSchema = z3.enum([
  "NOT_REGISTERED",
  "TIMEOUT",
  "UPSTREAM_ERROR",
  "NETWORK_ERROR",
  "INVALID_VEHICLE"
]);
var ProviderErrorSchema = z3.object({
  provider: ProviderNameSchema,
  code: RatingErrorCodeSchema,
  message: z3.string()
});
var DecodedVehicleSchema = z3.object({
  year: z3.number().int().optional(),
  make: z3.string().optional(),
  model: z3.string().optional()
});
var RateResponseSchema = z3.object({
  quote_id: z3.string().uuid().nullable(),
  decoded_vehicle: DecodedVehicleSchema.nullable(),
  rated_products: z3.array(RatedProductWireSchema),
  unavailable_providers: z3.array(ProviderNameSchema),
  errors: z3.array(ProviderErrorSchema)
});
var TestRateRequestSchema = z3.object({
  vehicle: VehicleInputSchema,
  rate_inputs: RateInputsSchema.optional()
});
var TestRateResponseSchema = z3.object({
  rated_products: z3.array(TestRatedProductWireSchema),
  raw_response: z3.record(z3.unknown()).optional()
});
var CatalogProductSchema = z3.object({
  product_id: z3.number().int(),
  name: z3.string(),
  product_type: z3.string().nullable(),
  provider_id: z3.number().int(),
  provider_name: z3.string()
});
var CatalogResponseSchema = z3.object({
  products: z3.array(CatalogProductSchema)
});
var ApiErrorCodeSchema = z3.enum([
  "NO_AUTH",
  "INVALID_JWT",
  "NO_WIDGET_API_KEY",
  "INVALID_WIDGET_API_KEY",
  "NO_BEARER_AUTH",
  "INVALID_API_KEY",
  "INVALID_TOKEN",
  "EXPIRED_OR_REVOKED",
  "OUTREACH_ALREADY_PURCHASED",
  "RATE_LIMITED",
  "BAD_REQUEST",
  "NOT_FOUND",
  "CONFLICT",
  "PROVIDER_DOES_NOT_SUPPORT_AUTO_REGISTRATION",
  "PROVIDER_DOES_NOT_SUPPORT_CATALOG_DISCOVERY",
  "STALE_RATE",
  "QUOTE_ALREADY_CONVERTED",
  "PAYMENT_PROVIDER_UNAVAILABLE",
  "SIGNATURE_SERVICE_UNAVAILABLE",
  // Distinct from NOT_FOUND so the admin can show "try again later" vs
  // "wrong link".
  "PDF_NOT_AVAILABLE",
  "ORDER_NOT_PAID",
  "ORDER_ALREADY_PAID",
  "PLATE_NOT_FOUND",
  // Distinct from PLATE_NOT_FOUND so the widget suggests VIN-mode instead of
  // retrying.
  "DECODE_UNAVAILABLE",
  "VIN_NOT_FOUND",
  "VIN_DECODE_UNAVAILABLE",
  "TAX_RATE_NOT_FOUND",
  // Distinct from TAX_RATE_NOT_FOUND so the admin can show "try again" vs
  // "fix the zip".
  "TAX_RATE_LOOKUP_UNAVAILABLE"
]);
var ApiErrorResponseSchema = z3.object({
  error: ApiErrorCodeSchema,
  message: z3.string()
});
var AuthenticatedUserSchema = z3.object({
  id: z3.string().uuid(),
  email: z3.string().email(),
  name: z3.string()
});
var MagicLinkRequestSchema = z3.object({
  email: z3.string().email()
});
var MagicLinkRequestResponseSchema = z3.object({
  ok: z3.literal(true)
});
var MagicLinkConsumeSchema = z3.object({
  // 32 bytes base64url-encoded.
  token: z3.string().regex(/^[A-Za-z0-9_-]{43}$/)
});
var MagicLinkConsumeResponseSchema = z3.object({
  jwt: z3.string(),
  user: AuthenticatedUserSchema
});
var MeResponseSchema = AuthenticatedUserSchema;
var IdParamsSchema = z3.object({
  id: z3.string().uuid()
});
var RESERVED_ACCOUNT_SLUGS = ["api", "health"];
var AccountSlugSchema = z3.string().max(40).regex(
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  "Use lowercase letters, numbers and single hyphens (e.g. tipton-ford)."
).refine((s) => !RESERVED_ACCOUNT_SLUGS.includes(s), "That word is reserved. Pick another.");
var HttpUrlSchema = z3.string().url("Must be a full URL (e.g. https://example.com).").refine((u) => /^https?:\/\//i.test(u), "Must start with http:// or https://.");
var FinanceMonthsSchema = z3.number().min(FINANCE_STEP_MONTHS).max(MAX_FINANCE_MONTHS).multipleOf(FINANCE_STEP_MONTHS);
var FinanceMinDownPaymentPctSchema = z3.number().nonnegative().lt(1);
var AccountSummarySchema = z3.object({
  id: z3.string().uuid(),
  name: z3.string(),
  website_url: z3.string().nullable(),
  budco_dealer_id: z3.string().min(1),
  created_at: z3.string()
});
var AccountListResponseSchema = z3.object({
  accounts: z3.array(AccountSummarySchema)
});
var CreateAccountRequestSchema = z3.object({
  name: z3.string().min(1),
  slug: AccountSlugSchema.nullable().optional(),
  budco_dealer_id: z3.string().min(1),
  website_url: HttpUrlSchema.nullable().optional(),
  logo_url: HttpUrlSchema.nullable().optional(),
  hosted: z3.boolean().optional(),
  default_tax_rate: z3.number().nonnegative().max(1).optional(),
  financing_fee_pct: z3.number().nonnegative().max(1).optional(),
  paid_in_full_fee_pct: z3.number().nonnegative().max(1).optional(),
  default_markup_pct: z3.number().nonnegative().optional(),
  default_fixed_markup: z3.number().nonnegative().optional(),
  default_sellmore_fee: z3.number().nonnegative().optional(),
  finance_min_months: FinanceMonthsSchema.optional(),
  finance_max_months: FinanceMonthsSchema.optional(),
  finance_min_down_payment_pct: FinanceMinDownPaymentPctSchema.optional(),
  brand_color: z3.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a 6-digit hex color (e.g. #c82068).").nullable().optional(),
  docusign_sender_email: z3.string().email().nullable().optional(),
  docusign_cc_emails: z3.array(z3.string().email()).optional(),
  street: z3.string().min(1).nullable().optional(),
  city: z3.string().min(1).nullable().optional(),
  state: z3.string().min(1).nullable().optional(),
  postal_code: z3.string().min(1).nullable().optional(),
  phone: z3.string().min(1).nullable().optional()
});
var UpdateAccountRequestSchema = z3.object({
  name: z3.string().min(1).optional(),
  slug: AccountSlugSchema.nullable().optional(),
  budco_dealer_id: z3.string().min(1).optional(),
  website_url: HttpUrlSchema.nullable().optional(),
  logo_url: HttpUrlSchema.nullable().optional(),
  hosted: z3.boolean().optional(),
  default_tax_rate: z3.number().nonnegative().max(1).optional(),
  financing_fee_pct: z3.number().nonnegative().max(1).optional(),
  paid_in_full_fee_pct: z3.number().nonnegative().max(1).optional(),
  default_markup_pct: z3.number().nonnegative().optional(),
  default_fixed_markup: z3.number().nonnegative().optional(),
  default_sellmore_fee: z3.number().nonnegative().optional(),
  finance_min_months: FinanceMonthsSchema.optional(),
  finance_max_months: FinanceMonthsSchema.optional(),
  finance_min_down_payment_pct: FinanceMinDownPaymentPctSchema.optional(),
  brand_color: z3.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a 6-digit hex color (e.g. #c82068).").nullable().optional(),
  docusign_sender_email: z3.string().email().nullable().optional(),
  docusign_cc_emails: z3.array(z3.string().email()).optional(),
  street: z3.string().min(1).nullable().optional(),
  city: z3.string().min(1).nullable().optional(),
  state: z3.string().min(1).nullable().optional(),
  postal_code: z3.string().min(1).nullable().optional(),
  phone: z3.string().min(1).nullable().optional()
});
var AccountDetailSchema = z3.object({
  id: z3.string().uuid(),
  name: z3.string(),
  slug: z3.string().nullable(),
  widget_api_key: z3.string(),
  budco_dealer_id: z3.string().min(1),
  default_tax_rate: z3.number().nonnegative(),
  financing_fee_pct: z3.number().nonnegative(),
  paid_in_full_fee_pct: z3.number().nonnegative(),
  default_markup_pct: z3.number().nonnegative(),
  default_fixed_markup: z3.number().nonnegative(),
  default_sellmore_fee: z3.number().nonnegative(),
  finance_min_months: z3.number().int(),
  finance_max_months: z3.number().int(),
  finance_min_down_payment_pct: z3.number().nonnegative(),
  brand_color: z3.string().nullable(),
  website_url: z3.string().nullable(),
  logo_url: z3.string().nullable(),
  hosted: z3.boolean(),
  docusign_sender_email: z3.string().nullable(),
  docusign_cc_emails: z3.array(z3.string()),
  street: z3.string().nullable(),
  city: z3.string().nullable(),
  state: z3.string().nullable(),
  postal_code: z3.string().nullable(),
  phone: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var AccountAdminDetailSchema = AccountDetailSchema.extend({
  install_url: z3.string().nullable(),
  install_snippet: z3.string().nullable()
});
var AccountFaqSchema = z3.object({
  id: z3.string().uuid(),
  account_id: z3.string().uuid(),
  question: z3.string(),
  answer: z3.string(),
  position: z3.number().int().nonnegative(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var AccountFaqListResponseSchema = z3.object({
  account_faqs: z3.array(AccountFaqSchema)
});
var CreateAccountFaqRequestSchema = z3.object({
  account_id: z3.string().uuid(),
  question: z3.string().trim().min(1).max(200),
  answer: z3.string().trim().min(1).max(2e3)
});
var UpdateAccountFaqRequestSchema = z3.object({
  question: z3.string().trim().min(1).max(200).optional(),
  answer: z3.string().trim().min(1).max(2e3).optional()
});
var ReorderAccountFaqsRequestSchema = z3.object({
  account_id: z3.string().uuid(),
  faq_ids: z3.array(z3.string().uuid()).min(1)
});
var CopyAccountFaqsRequestSchema = z3.object({
  target_account_id: z3.string().uuid(),
  source_account_id: z3.string().uuid(),
  faq_ids: z3.array(z3.string().uuid()).optional()
});
var CopyAccountFaqsResponseSchema = z3.object({
  copied: z3.array(AccountFaqSchema),
  skipped: z3.array(
    z3.object({
      source_faq_id: z3.string().uuid(),
      reason: z3.literal("DUPLICATE_QUESTION"),
      question: z3.string()
    })
  )
});
var ApiClientSummarySchema = z3.object({
  id: z3.string().uuid(),
  name: z3.string(),
  enabled: z3.boolean(),
  created_at: z3.string()
});
var ApiClientListResponseSchema = z3.object({
  api_clients: z3.array(ApiClientSummarySchema)
});
var CreateApiClientRequestSchema = z3.object({
  name: z3.string().min(1)
});
var UpdateApiClientRequestSchema = z3.object({
  name: z3.string().min(1).optional(),
  enabled: z3.boolean().optional()
});
var ApiClientDetailSchema = z3.object({
  id: z3.string().uuid(),
  name: z3.string(),
  api_key: z3.string(),
  enabled: z3.boolean(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var ApiClientUsageSchema = z3.object({
  total_requests: z3.number().int(),
  reconsideration_requests: z3.number().int(),
  renewal_requests: z3.number().int(),
  responded_count: z3.number().int(),
  last_request_at: z3.string().nullable()
});
var QuoteSummarySchema = z3.object({
  id: z3.string().uuid(),
  account_id: z3.string().uuid(),
  account_name: z3.string(),
  customer_id: z3.string().uuid().nullable(),
  customer_first_name: z3.string().nullable(),
  customer_last_name: z3.string().nullable(),
  vehicle_vin: z3.string(),
  vehicle_year: z3.number().int().nullable(),
  vehicle_make: z3.string().nullable(),
  vehicle_model: z3.string().nullable(),
  type: FlowTypeSchema,
  item_count: z3.number().int().nonnegative(),
  linked_order_id: z3.string().uuid().nullable(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var QuoteListResponseSchema = z3.object({
  quotes: z3.array(QuoteSummarySchema),
  ...paginatedListFields
});
var QuoteItemDetailSchema = z3.object({
  id: z3.string().uuid(),
  product_id: z3.string().uuid(),
  product_name: z3.string(),
  provider: ProviderNameSchema,
  coverage_name: z3.string(),
  product_type: z3.string(),
  term_months: z3.number().int().nonnegative(),
  term_miles: z3.number().int().nonnegative(),
  deductible_amount: z3.number().nullable(),
  deductible_type: z3.string().nullable(),
  base_price: z3.number().nonnegative(),
  markup_pct: z3.number().nonnegative(),
  fixed_markup: z3.number().nonnegative(),
  sellmore_fee: z3.number().nonnegative(),
  financing_fee_pct: z3.number().nonnegative(),
  paid_in_full_fee_pct: z3.number().nonnegative(),
  dealer_cost: z3.number().nullable(),
  tax_rate: z3.number().nonnegative(),
  sale_price_finance: z3.number().nonnegative(),
  sale_price_pif: z3.number().nonnegative(),
  taxes_finance: z3.number().nonnegative(),
  taxes_pif: z3.number().nonnegative(),
  total_finance: z3.number().nonnegative(),
  total_pif: z3.number().nonnegative(),
  margin_finance: z3.number().nullable(),
  margin_pif: z3.number().nullable(),
  surcharges: z3.array(SurchargeWireSchema),
  created_at: z3.string(),
  updated_at: z3.string()
});
var QuoteDetailSchema = z3.object({
  id: z3.string().uuid(),
  type: FlowTypeSchema,
  source_product_ids: z3.array(z3.string().uuid()),
  created_at: z3.string(),
  updated_at: z3.string(),
  account: z3.object({
    id: z3.string().uuid(),
    name: z3.string()
  }),
  vehicle: z3.object({
    vin: z3.string(),
    year: z3.number().int().nullable(),
    make: z3.string().nullable(),
    model: z3.string().nullable(),
    mileage: z3.number().int().nonnegative()
  }),
  customer: z3.object({
    id: z3.string().uuid(),
    first_name: z3.string(),
    last_name: z3.string(),
    email: z3.string().nullable(),
    phone: z3.string()
  }).nullable(),
  items: z3.array(QuoteItemDetailSchema),
  linked_order_id: z3.string().uuid().nullable(),
  linked_outreach_id: z3.string().uuid().nullable()
});
var PaymentTypeSchema = z3.enum(["pay_in_full", "finance"]);
var OrderPaymentSummarySchema = z3.enum(["pay_in_full", "finance", "mixed"]);
var DecodePlateRequestSchema = z3.object({
  plate: z3.string().min(1).max(15),
  state: z3.string().length(2)
});
var DecodePlateResponseSchema = z3.object({
  vin: z3.string().length(17),
  year: z3.number().int().optional(),
  make: z3.string().optional(),
  model: z3.string().optional(),
  trim: z3.string().optional()
});
var DecodeVinRequestSchema = z3.object({
  vin: z3.string().length(17)
});
var DecodeVinResponseSchema = z3.object({
  vin: z3.string().length(17),
  year: z3.number().int().optional(),
  make: z3.string().optional(),
  model: z3.string().optional()
});
var LookupTaxRateRequestSchema = z3.object({
  postal_code: z3.string().regex(/^\d{5}$/, "Postal code must be 5 digits.")
});
var LookupTaxRateResponseSchema = z3.object({
  // Decimal, e.g. 0.08875 for 8.875%. Same shape as accounts.default_tax_rate.
  rate: z3.number().nonnegative().max(1),
  city: z3.string().optional(),
  state: z3.string().optional()
});
var ConfigFaqSchema = z3.object({
  question: z3.string(),
  answer: z3.string()
});
var ConfigResponseSchema = z3.object({
  account_name: z3.string(),
  brand_color: z3.string().nullable(),
  website_url: z3.string().nullable(),
  phone: z3.string().nullable(),
  /**
   * Whether SellMore hosts this account's page, and its URL where the environment mints one. The
   * widget renders neither; they are here for the preview harness, which holds a widget key and
   * nothing else, and so has no other way to tell whether an account pastes a snippet or has a page
   * of its own to open.
   *
   * Two fields rather than one nullable URL, because null has two causes: a hosted account in an
   * environment with no HOSTED_BASE mints no link, and inferring `hosted` from the URL would report
   * that as "not hosted".
   */
  hosted: z3.boolean(),
  hosted_url: z3.string().nullable(),
  rate_inputs: RateInputRequirementsSchema,
  /** Empty means the account has no FAQ and the widget renders no section. */
  faqs: z3.array(ConfigFaqSchema),
  /** The account's Budco limits, so the payment controls offer only what checkout accepts. */
  finance_min_months: z3.number().int(),
  finance_max_months: z3.number().int(),
  finance_min_down_payment_pct: z3.number().nonnegative()
});
var ConfigResponseCompatSchema = ConfigResponseSchema.partial({
  hosted: true,
  hosted_url: true,
  faqs: true,
  finance_min_months: true,
  finance_max_months: true,
  finance_min_down_payment_pct: true
});
var CheckoutAddressSchema = RateBuyerAddressSchema.extend({ country: z3.string().min(1) });
var CheckoutCustomerSchema = z3.object({
  first_name: z3.string().min(1),
  last_name: z3.string().min(1),
  email: z3.string().email(),
  phone: NormalizedPhoneSchema,
  address: CheckoutAddressSchema
});
var CheckoutVehicleSchema = z3.object({
  vin: z3.string().length(17),
  year: z3.number().int().min(1900).max(2100),
  make: z3.string().min(1),
  model: z3.string().min(1),
  mileage: z3.number().int().nonnegative()
});
var CheckoutCartSurchargeSchema = z3.object({
  code: z3.string().min(1),
  selected: z3.boolean()
});
var CartItemFinancingSchema = z3.object({
  duration_months: z3.number().int().positive(),
  down_payment_amount: z3.number().nonnegative()
});
var CheckoutCartItemSchema = z3.discriminatedUnion("payment_type", [
  z3.object({
    payment_type: z3.literal("pay_in_full"),
    quote_item_id: z3.string().uuid(),
    surcharges: z3.array(CheckoutCartSurchargeSchema)
  }),
  z3.object({
    payment_type: z3.literal("finance"),
    quote_item_id: z3.string().uuid(),
    surcharges: z3.array(CheckoutCartSurchargeSchema),
    financing: CartItemFinancingSchema
  })
]);
var CheckoutRequestSchema = z3.object({
  quote_id: z3.string().uuid(),
  customer: CheckoutCustomerSchema,
  vehicle: CheckoutVehicleSchema,
  cart: z3.array(CheckoutCartItemSchema).min(1),
  success_url: HttpUrlSchema
});
var CheckoutResponseSchema = z3.object({
  order_id: z3.string().uuid(),
  payment_link: HttpUrlSchema
});
var OrderSummarySchema = z3.object({
  id: z3.string().uuid(),
  quote_id: z3.string().uuid(),
  account_id: z3.string().uuid(),
  account_name: z3.string(),
  customer_id: z3.string().uuid().nullable(),
  customer_first_name: z3.string().nullable(),
  customer_last_name: z3.string().nullable(),
  vehicle_vin: z3.string(),
  vehicle_year: z3.number().int().nullable(),
  vehicle_make: z3.string().nullable(),
  vehicle_model: z3.string().nullable(),
  payment_summary: OrderPaymentSummarySchema,
  item_count: z3.number().int().nonnegative(),
  total_sale_price: z3.number().nonnegative(),
  paid_at: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var OrderListResponseSchema = z3.object({
  orders: z3.array(OrderSummarySchema),
  ...paginatedListFields
});
var OrderItemDetailSchema = z3.object({
  id: z3.string().uuid(),
  product_id: z3.string().uuid(),
  payment_type: PaymentTypeSchema,
  financing_duration_months: z3.number().int().nullable(),
  down_payment_percentage: z3.number().nullable(),
  down_payment_amount: z3.number().nullable(),
  financing_amount: z3.number().nullable(),
  product_name: z3.string(),
  provider: ProviderNameSchema,
  coverage_name: z3.string(),
  product_type: z3.string(),
  term_months: z3.number().int().nonnegative(),
  term_miles: z3.number().int().nonnegative(),
  deductible_amount: z3.number().nullable(),
  deductible_type: z3.string().nullable(),
  base_price: z3.number().nonnegative(),
  markup_pct: z3.number().nonnegative(),
  fixed_markup: z3.number().nonnegative(),
  sellmore_fee: z3.number().nonnegative(),
  financing_fee_pct: z3.number().nonnegative(),
  paid_in_full_fee_pct: z3.number().nonnegative(),
  dealer_cost: z3.number().nullable(),
  tax_rate: z3.number().nonnegative(),
  sale_price: z3.number().nonnegative(),
  taxes: z3.number().nonnegative(),
  total: z3.number().nonnegative(),
  margin: z3.number().nullable(),
  surcharges: z3.array(SurchargeWireSchema),
  paid_at: z3.string().nullable(),
  contract_issued_at: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var WarrantyContractDetailSchema = z3.object({
  id: z3.string().uuid(),
  order_item_id: z3.string().uuid(),
  contract_number: z3.string(),
  starting_mileage: z3.number().int(),
  effective_date: z3.string(),
  expiration_date: z3.string(),
  docusign_envelope_id: z3.string().nullable(),
  signed_at: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var InstallmentContractDetailSchema = z3.object({
  id: z3.string().uuid(),
  order_item_id: z3.string().uuid(),
  provider: z3.literal("budco"),
  docusign_envelope_id: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string()
});
var OrderDetailSchema = z3.object({
  id: z3.string().uuid(),
  payment_summary: OrderPaymentSummarySchema,
  payment_reference: z3.string().nullable(),
  paid_at: z3.string().nullable(),
  success_url: z3.string(),
  ric_pdf_url: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string(),
  quote: z3.object({
    id: z3.string().uuid(),
    type: FlowTypeSchema
  }),
  account: z3.object({
    id: z3.string().uuid(),
    name: z3.string()
  }),
  vehicle: z3.object({
    vin: z3.string(),
    year: z3.number().int().nullable(),
    make: z3.string().nullable(),
    model: z3.string().nullable(),
    mileage: z3.number().int().nonnegative()
  }),
  customer: z3.object({
    id: z3.string().uuid(),
    first_name: z3.string(),
    last_name: z3.string(),
    email: z3.string().nullable(),
    phone: z3.string()
  }).nullable(),
  /** Frozen at checkout; `customer` above is the live row a later merge can change. */
  buyer_snapshot: z3.object({
    first_name: z3.string(),
    last_name: z3.string(),
    email: z3.string().nullable(),
    phone: z3.string(),
    address: z3.record(z3.string(), z3.unknown()).nullable()
  }),
  items: z3.array(OrderItemDetailSchema),
  warranty_contracts: z3.array(WarrantyContractDetailSchema),
  installment_contracts: z3.array(InstallmentContractDetailSchema)
});
var WarrantyContractPdfResponseSchema = z3.object({
  presigned_url: HttpUrlSchema,
  kind: z3.enum(["issued", "signed"])
});
var IssueContractFailureSchema = z3.object({
  order_item_id: z3.string().uuid(),
  code: z3.string(),
  message: z3.string(),
  retryable: z3.boolean()
});
var MarkPaidResponseSchema = z3.object({
  issued: z3.number().int().nonnegative(),
  failed: z3.array(IssueContractFailureSchema),
  envelopes_created: z3.number().int().nonnegative(),
  envelopes_failed: z3.array(
    z3.object({
      warranty_contract_id: z3.string().uuid(),
      message: z3.string()
    })
  )
});
var MarkPaidRequestSchema = z3.object({
  ric_url: HttpUrlSchema.optional()
});
var OrderItemParamsSchema = z3.object({
  id: z3.string().uuid(),
  item_id: z3.string().uuid()
});
var UploadContractRequestSchema = z3.object({
  contract_number: z3.string().trim().min(1),
  pdf_base64: z3.string().min(1)
});
var GenerateContractResponseSchema = z3.object({
  issued_order_item_ids: z3.array(z3.string().uuid()),
  failed: z3.array(IssueContractFailureSchema)
});
var OutreachIdParamsSchema = z3.object({
  outreach_id: z3.string().uuid()
});
var OutreachTypeSchema = z3.enum(["reconsideration", "renewal"]);
var OutreachChannelSchema = z3.enum(["sms", "email"]);
var OutreachChannelsSchema = z3.array(OutreachChannelSchema).min(1);
var OutreachEnrollmentStatusSchema = z3.enum([
  "scheduled",
  "active",
  "completed",
  "stopped"
]);
var OutreachExitReasonSchema = z3.enum([
  "purchased",
  "revoked",
  "campaign_disabled",
  "expired",
  "no_active_products",
  "no_link",
  "undeliverable"
]);
var OutreachDeliveryStatusSchema = z3.enum(["pending", "sent", "failed", "cancelled"]);
var OutreachDeliverySchema = z3.object({
  step_index: z3.number().int().nonnegative(),
  channel: OutreachChannelSchema,
  status: OutreachDeliveryStatusSchema,
  scheduled_for: z3.string(),
  sent_at: z3.string().nullable(),
  recipient: z3.string().nullable(),
  failure_reason: z3.string().nullable()
});
var OutreachDisplayStatusSchema = z3.enum([
  "scheduled",
  "in_progress",
  "completed",
  "purchased",
  "revoked",
  "stopped"
]);
var OutreachCustomerInputSchema = z3.object({
  first_name: z3.string().min(1),
  last_name: z3.string().min(1),
  email: z3.string().email().nullable().optional(),
  phone: NormalizedPhoneSchema,
  address: z3.record(z3.unknown()).nullable().optional()
});
var OutreachVehicleInputSchema = z3.object({
  vin: z3.string().length(17),
  year: z3.number().int().nullable().optional(),
  make: z3.string().nullable().optional(),
  model: z3.string().nullable().optional(),
  mileage: z3.number().int().nonnegative()
});
var OutreachReconsiderationInboundRequestSchema = z3.object({
  // The target account (our accounts.id). Resolved + validated server-side.
  account_id: z3.string().uuid(),
  external_reference: z3.string().min(1),
  customer: OutreachCustomerInputSchema,
  vehicle: OutreachVehicleInputSchema,
  declined_product_types: z3.array(z3.string().min(1)),
  acquired_product_types: z3.array(z3.string().min(1))
});
var OutreachRenewalInboundRequestSchema = z3.object({
  // The target account (our accounts.id). Resolved + validated server-side.
  account_id: z3.string().uuid(),
  external_reference: z3.string().min(1),
  customer: OutreachCustomerInputSchema,
  vehicle: OutreachVehicleInputSchema,
  source_product_types: z3.array(z3.string().min(1)).min(1),
  contract_expires_at: z3.string().datetime()
}).refine((b) => new Date(b.contract_expires_at).getTime() > Date.now(), {
  message: "Contract expiry must be in the future.",
  path: ["contract_expires_at"]
});
var OutreachEnrolledResponseSchema = z3.object({
  status: z3.literal("enrolled"),
  outreach_id: z3.string().uuid(),
  message_count: z3.number().int().positive(),
  first_message_at: z3.string(),
  channels: z3.array(OutreachChannelSchema),
  mapped_product_count: z3.number().int().nonnegative(),
  dropped_product_types: z3.array(z3.string())
});
var OutreachSkippedNoActiveProductsSchema = z3.object({
  status: z3.literal("skipped_no_active_products")
});
var OutreachSkippedCampaignDisabledSchema = z3.object({
  status: z3.literal("skipped_campaign_disabled")
});
var OutreachInboundResponseSchema = z3.discriminatedUnion("status", [
  OutreachEnrolledResponseSchema,
  OutreachSkippedNoActiveProductsSchema,
  OutreachSkippedCampaignDisabledSchema
]);
var OutreachReconsiderationInboundResponseSchema = z3.discriminatedUnion("status", [
  OutreachEnrolledResponseSchema,
  OutreachSkippedNoActiveProductsSchema,
  OutreachSkippedCampaignDisabledSchema,
  z3.object({ status: z3.literal("skipped_fully_covered") }),
  z3.object({ status: z3.literal("skipped_nothing_declined") })
]);
var OutreachSeedResponseSchema = z3.object({
  type: OutreachTypeSchema,
  source_product_ids: z3.array(z3.string().uuid()),
  customer: z3.object({
    id: z3.string().uuid(),
    first_name: z3.string(),
    last_name: z3.string(),
    email: z3.string().nullable(),
    phone: z3.string()
  }),
  vehicle: z3.object({
    vin: z3.string(),
    year: z3.number().int().nullable(),
    make: z3.string().nullable(),
    model: z3.string().nullable(),
    mileage: z3.number().int()
  })
});
var AdminOutreachListQuerySchema = z3.object({
  type: OutreachTypeSchema.optional(),
  // "Outreach we actually tried on this channel" — matched against the delivery rows, not the
  // campaign's current setting, so toggling a channel off never rewrites history.
  channel: OutreachChannelSchema.optional(),
  status: OutreachDisplayStatusSchema.optional(),
  account_id: z3.string().uuid().optional(),
  api_client_id: z3.string().uuid().optional(),
  from_date: z3.string().datetime().optional(),
  to_date: z3.string().datetime().optional(),
  sort_by: z3.enum(["created_at", "updated_at"]).optional().default("created_at"),
  order: sortOrderField,
  ...paginationQueryFields
});
var AdminOutreachSummarySchema = z3.object({
  id: z3.string().uuid(),
  account: z3.object({ id: z3.string().uuid(), name: z3.string() }),
  customer: z3.object({
    id: z3.string().uuid(),
    first_name: z3.string(),
    last_name: z3.string()
  }),
  type: OutreachTypeSchema,
  status: OutreachEnrollmentStatusSchema,
  exit_reason: OutreachExitReasonSchema.nullable(),
  display_status: OutreachDisplayStatusSchema,
  external_reference: z3.string().nullable(),
  messages_sent: z3.number().int().nonnegative(),
  messages_total: z3.number().int().nonnegative(),
  first_message_at: z3.string().nullable(),
  opened_at: z3.string().nullable(),
  responded_at: z3.string().nullable(),
  exited_at: z3.string().nullable(),
  expires_at: z3.string().nullable(),
  created_at: z3.string()
});
var AdminOutreachQuoteSchema = z3.object({
  id: z3.string().uuid(),
  item_count: z3.number().int().nonnegative(),
  linked_order_id: z3.string().uuid().nullable(),
  created_at: z3.string()
});
var AdminOutreachListResponseSchema = z3.object({
  outreach: z3.array(AdminOutreachSummarySchema),
  ...paginatedListFields
});
var AdminOutreachDetailSchema = z3.object({
  id: z3.string().uuid(),
  account: z3.object({ id: z3.string().uuid(), name: z3.string() }),
  customer: z3.object({
    id: z3.string().uuid(),
    first_name: z3.string(),
    last_name: z3.string(),
    email: z3.string().nullable(),
    phone: z3.string()
  }),
  vehicle: z3.object({
    vin: z3.string(),
    year: z3.number().int().nullable(),
    make: z3.string().nullable(),
    model: z3.string().nullable(),
    mileage: z3.number().int()
  }).nullable(),
  type: OutreachTypeSchema,
  status: OutreachEnrollmentStatusSchema,
  exit_reason: OutreachExitReasonSchema.nullable(),
  display_status: OutreachDisplayStatusSchema,
  exit_note: z3.string().nullable(),
  external_reference: z3.string().nullable(),
  // Every message this enrollment will ever have, written at enrollment and never recomputed.
  deliveries: z3.array(OutreachDeliverySchema),
  messages_sent: z3.number().int().nonnegative(),
  messages_total: z3.number().int().nonnegative(),
  next_message_at: z3.string().nullable(),
  first_message_at: z3.string().nullable(),
  opened_at: z3.string().nullable(),
  responded_at: z3.string().nullable(),
  exited_at: z3.string().nullable(),
  expires_at: z3.string().nullable(),
  created_at: z3.string(),
  updated_at: z3.string(),
  source_products: z3.array(z3.object({ id: z3.string().uuid(), name: z3.string() })),
  // Recon-only audit trail; empty arrays for renewal + admin-triggered rows.
  declined_product_types: z3.array(z3.string()),
  acquired_product_types: z3.array(z3.string()),
  // Provenance for partner-pushed recon/renewal rows; null for
  // admin-scheduled rows.
  api_client: z3.object({ id: z3.string().uuid(), name: z3.string() }).nullable(),
  // Newest first. More than one means they came back and re-rated before deciding.
  quotes: z3.array(AdminOutreachQuoteSchema)
});
var AdminOutreachCreateRequestSchema = z3.object({
  type: OutreachTypeSchema,
  customer_id: z3.string().uuid().optional(),
  account_id: z3.string().uuid().optional(),
  customer: OutreachCustomerInputSchema.optional(),
  vehicle: OutreachVehicleInputSchema.optional(),
  source_product_ids: z3.array(z3.string().uuid()).optional(),
  // Renewal schedules backwards from the contract's expiry, so the form collects it.
  contract_expires_at: z3.string().datetime().optional(),
  /** Skips the campaign's offset: the first message goes on the next drain tick. */
  send_now: z3.boolean().optional()
}).refine((b) => (b.customer_id ? 1 : 0) + (b.customer ? 1 : 0) === 1, {
  message: "Exactly one of customer_id or customer is required."
}).refine((b) => !b.customer || !!b.account_id, {
  message: "account_id is required when customer is provided.",
  path: ["account_id"]
}).refine((b) => b.type !== "renewal" || !!b.contract_expires_at, {
  message: "Contract expiry is required for a renewal.",
  path: ["contract_expires_at"]
}).refine((b) => !b.contract_expires_at || new Date(b.contract_expires_at).getTime() > Date.now(), {
  message: "Contract expiry must be in the future.",
  path: ["contract_expires_at"]
});
var AdminOutreachCreateResponseSchema = z3.object({
  outreach_id: z3.string().uuid()
});
var AdminOutreachRevokeRequestSchema = z3.object({
  reason: z3.string().min(1).optional()
});
var AdminOutreachRevokeResponseSchema = z3.object({
  ok: z3.literal(true)
});
var OUTREACH_TEMPLATE_VARS = [
  "customer_first_name",
  "customer_last_name",
  "account_name",
  "vehicle_year",
  "vehicle_make",
  "vehicle_model",
  "outreach_url"
];
var OutreachCampaignStepSchema = z3.object({
  step_index: z3.number().int().min(0).max(MAX_OUTREACH_STEPS - 1),
  delay_days: z3.number().int().min(0).max(365),
  // Four GSM-7 segments, before placeholders expand. Carriers split anything longer and bill per
  // segment per recipient, so an unbounded box is a cost an operator cannot see themselves spend.
  sms_body: z3.string().trim().min(1).max(612),
  email_subject: z3.string().trim().min(1).max(200),
  email_body: z3.string().trim().min(1).max(2e4)
});
var OutreachCampaignSchema = z3.object({
  type: OutreachTypeSchema,
  enabled: z3.boolean(),
  channels: OutreachChannelsSchema,
  /** Reconsideration counts forward from ingest; renewal back from `contract_expires_at`. */
  initial_offset_days: z3.number().int().min(0).max(365),
  /** Link life after the most recent message; null never expires. */
  link_expires_days: z3.number().int().min(1).max(365).nullable(),
  steps: z3.array(OutreachCampaignStepSchema).min(1).max(MAX_OUTREACH_STEPS)
});
var AccountOutreachCampaignsResponseSchema = z3.object({
  campaigns: z3.array(OutreachCampaignSchema)
});
var OutreachCampaignTypeParamsSchema = z3.object({
  type: OutreachTypeSchema
});
var UpdateOutreachCampaignRequestSchema = OutreachCampaignSchema.omit({
  type: true
}).superRefine((campaign, ctx) => {
  campaign.steps.forEach((step, i) => {
    if (step.step_index !== i) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "Steps must be numbered consecutively from 0.",
        path: ["steps", i, "step_index"]
      });
    }
    const gapRequired = i > 0;
    if (gapRequired && step.delay_days < 1) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "Leave at least one day between messages.",
        path: ["steps", i, "delay_days"]
      });
    }
    if (!gapRequired && step.delay_days !== 0) {
      ctx.addIssue({
        code: z3.ZodIssueCode.custom,
        message: "The first message is positioned by the campaign offset, not by a gap.",
        path: ["steps", i, "delay_days"]
      });
    }
    for (const [field, body] of [
      ["sms_body", step.sms_body],
      ["email_subject", step.email_subject],
      ["email_body", step.email_body]
    ]) {
      for (const [, name] of body.matchAll(/\{([a-z][a-z0-9_]*)\}/g)) {
        if (!OUTREACH_TEMPLATE_VARS.includes(name)) {
          ctx.addIssue({
            code: z3.ZodIssueCode.custom,
            message: `Unknown placeholder {${name}}.`,
            path: ["steps", i, field]
          });
        }
      }
    }
  });
});
var UpdateOutreachCampaignResponseSchema = z3.object({
  campaign: OutreachCampaignSchema,
  cancelled_message_count: z3.number().int().nonnegative()
});
var CustomerListQuerySchema = z3.object({
  account_id: z3.string().uuid().optional(),
  q: z3.string().optional(),
  sort_by: z3.enum(["created_at", "last_activity_at", "name"]).optional().default("created_at"),
  order: sortOrderField,
  ...paginationQueryFields
});
var CustomerSummarySchema = z3.object({
  id: z3.string().uuid(),
  account_id: z3.string().uuid(),
  account_name: z3.string(),
  first_name: z3.string(),
  last_name: z3.string(),
  email: z3.string().nullable(),
  phone: z3.string(),
  order_count: z3.number().int().nonnegative(),
  last_activity_at: z3.string().nullable(),
  created_at: z3.string()
});
var CustomerListResponseSchema = z3.object({
  customers: z3.array(CustomerSummarySchema),
  ...paginatedListFields
});
var CustomerDetailOrderSchema = z3.object({
  id: z3.string().uuid(),
  payment_summary: OrderPaymentSummarySchema,
  vehicle_vin: z3.string(),
  vehicle_year: z3.number().int().nullable(),
  vehicle_make: z3.string().nullable(),
  vehicle_model: z3.string().nullable(),
  item_count: z3.number().int().nonnegative(),
  total_sale_price: z3.number().nonnegative(),
  paid_at: z3.string().nullable(),
  created_at: z3.string()
});
var CustomerDetailQuoteSchema = z3.object({
  id: z3.string().uuid(),
  type: FlowTypeSchema,
  vehicle_vin: z3.string(),
  vehicle_year: z3.number().int().nullable(),
  vehicle_make: z3.string().nullable(),
  vehicle_model: z3.string().nullable(),
  item_count: z3.number().int().nonnegative(),
  linked_order_id: z3.string().uuid().nullable(),
  created_at: z3.string()
});
var CustomerDetailOutreachSchema = z3.object({
  id: z3.string().uuid(),
  type: OutreachTypeSchema,
  display_status: OutreachDisplayStatusSchema,
  messages_sent: z3.number().int().nonnegative(),
  messages_total: z3.number().int().nonnegative(),
  first_message_at: z3.string().nullable(),
  responded_at: z3.string().nullable(),
  created_at: z3.string()
});
var CustomerDetailWarrantyContractSchema = z3.object({
  id: z3.string().uuid(),
  order_id: z3.string().uuid(),
  order_item_id: z3.string().uuid(),
  contract_number: z3.string(),
  effective_date: z3.string(),
  expiration_date: z3.string(),
  signed_at: z3.string().nullable()
});
var CustomerDetailSchema = z3.object({
  id: z3.string().uuid(),
  account: z3.object({ id: z3.string().uuid(), name: z3.string() }),
  first_name: z3.string(),
  last_name: z3.string(),
  email: z3.string().nullable(),
  phone: z3.string(),
  address: z3.record(z3.string(), z3.unknown()).nullable(),
  created_at: z3.string(),
  updated_at: z3.string(),
  orders: z3.array(CustomerDetailOrderSchema),
  quotes: z3.array(CustomerDetailQuoteSchema),
  outreach_messages: z3.array(CustomerDetailOutreachSchema),
  warranty_contracts: z3.array(CustomerDetailWarrantyContractSchema)
});

// src/format.ts
var usdFmt = new Intl.NumberFormat(void 0, {
  style: "currency",
  currency: "USD"
});
function formatUsd(amount) {
  return usdFmt.format(amount);
}
var UNLIMITED_TERM_MILES = 999999;
var milesFmt = new Intl.NumberFormat(void 0);
function formatTermMiles(miles) {
  return miles >= UNLIMITED_TERM_MILES ? "Unlimited mi" : `${milesFmt.format(miles)} mi`;
}
function formatVin(input) {
  return input.replace(/\s+/g, "").toUpperCase();
}

// src/constants.ts
var US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
];
export {
  ALLIED_ACCOUNT_FIELDS,
  ALLIED_PRODUCT_FIELDS,
  ALLIED_RATE_INPUTS,
  ASCENT_ACCOUNT_FIELDS,
  ASCENT_PRODUCT_FIELDS,
  AccountAdminDetailSchema,
  AccountDetailSchema,
  AccountFaqListResponseSchema,
  AccountFaqSchema,
  AccountListResponseSchema,
  AccountOutreachCampaignsResponseSchema,
  AccountProviderListResponseSchema,
  AccountProviderSchema,
  AccountSlugSchema,
  AccountSummarySchema,
  AdminOutreachCreateRequestSchema,
  AdminOutreachCreateResponseSchema,
  AdminOutreachDetailSchema,
  AdminOutreachListQuerySchema,
  AdminOutreachListResponseSchema,
  AdminOutreachQuoteSchema,
  AdminOutreachRevokeRequestSchema,
  AdminOutreachRevokeResponseSchema,
  AdminOutreachSummarySchema,
  ApiClientDetailSchema,
  ApiClientListResponseSchema,
  ApiClientSummarySchema,
  ApiClientUsageSchema,
  ApiErrorCodeSchema,
  ApiErrorResponseSchema,
  AuthenticatedUserSchema,
  BUNDLE_PRODUCT_TYPES,
  CatalogProductSchema,
  CatalogResponseSchema,
  CheckoutRequestSchema,
  CheckoutResponseSchema,
  ConfigFaqSchema,
  ConfigResponseCompatSchema,
  ConfigResponseSchema,
  CopyAccountFaqsRequestSchema,
  CopyAccountFaqsResponseSchema,
  CopyProductsRequestSchema,
  CopyProductsResponseSchema,
  CreateAccountFaqRequestSchema,
  CreateAccountProviderRequestSchema,
  CreateAccountRequestSchema,
  CreateApiClientRequestSchema,
  CreateProductRequestSchema,
  CustomerDetailOrderSchema,
  CustomerDetailOutreachSchema,
  CustomerDetailQuoteSchema,
  CustomerDetailSchema,
  CustomerDetailWarrantyContractSchema,
  CustomerListQuerySchema,
  CustomerListResponseSchema,
  CustomerSummarySchema,
  DEFAULT_FINANCE_LIMITS,
  DecodePlateRequestSchema,
  DecodePlateResponseSchema,
  DecodeVinRequestSchema,
  DecodeVinResponseSchema,
  DecodedVehicleSchema,
  FINANCE_STEP_MONTHS,
  FlowTypeSchema,
  GenerateContractResponseSchema,
  HttpUrlSchema,
  IdParamsSchema,
  InstallmentContractDetailSchema,
  LookupTaxRateRequestSchema,
  LookupTaxRateResponseSchema,
  MAX_FINANCE_MONTHS,
  MAX_OUTREACH_STEPS,
  MagicLinkConsumeResponseSchema,
  MagicLinkConsumeSchema,
  MagicLinkRequestResponseSchema,
  MagicLinkRequestSchema,
  MarkPaidRequestSchema,
  MarkPaidResponseSchema,
  MeResponseSchema,
  NO_RATE_INPUTS,
  OUTREACH_PROVIDERS,
  OUTREACH_TEMPLATE_VARS,
  OrderDetailSchema,
  OrderItemDetailSchema,
  OrderItemParamsSchema,
  OrderListResponseSchema,
  OrderPaymentSummarySchema,
  OrderSummarySchema,
  OutreachCampaignSchema,
  OutreachCampaignStepSchema,
  OutreachCampaignTypeParamsSchema,
  OutreachChannelSchema,
  OutreachChannelsSchema,
  OutreachCustomerInputSchema,
  OutreachDeliverySchema,
  OutreachDeliveryStatusSchema,
  OutreachDisplayStatusSchema,
  OutreachEnrollmentStatusSchema,
  OutreachExitReasonSchema,
  OutreachIdParamsSchema,
  OutreachInboundResponseSchema,
  OutreachReconsiderationInboundRequestSchema,
  OutreachReconsiderationInboundResponseSchema,
  OutreachRenewalInboundRequestSchema,
  OutreachSeedResponseSchema,
  OutreachTypeSchema,
  OutreachVehicleInputSchema,
  PCRS_ACCOUNT_FIELDS,
  PCRS_PRODUCT_FIELDS,
  PEN_ACCOUNT_FIELDS,
  PEN_PRODUCT_FIELDS,
  PRODUCT_TYPES,
  PaymentTypeSchema,
  PricingService,
  ProductListResponseSchema,
  ProductSchema,
  ProviderErrorSchema,
  ProviderNameSchema,
  ProvidersDiscoveryResponseSchema,
  QuoteDetailSchema,
  QuoteItemDetailSchema,
  QuoteListResponseSchema,
  QuoteSummarySchema,
  RateBuyerAddressSchema,
  RateInputRequirementsSchema,
  RateInputSpecSchema,
  RateInputsSchema,
  RateRequestSchema,
  RateResponseSchema,
  RatedProductWireSchema,
  RatingErrorCodeSchema,
  ReorderAccountFaqsRequestSchema,
  RichTextDocSchema,
  SurchargeSchema,
  SurchargeWireSchema,
  TestRateRequestSchema,
  TestRateResponseSchema,
  TestRatedProductWireSchema,
  TypedFieldSpecSchema,
  US_STATES,
  UpdateAccountFaqRequestSchema,
  UpdateAccountProviderRequestSchema,
  UpdateAccountRequestSchema,
  UpdateApiClientRequestSchema,
  UpdateOutreachCampaignRequestSchema,
  UpdateOutreachCampaignResponseSchema,
  UpdateProductRequestSchema,
  UploadContractRequestSchema,
  VehicleInputSchema,
  WarrantyContractDetailSchema,
  WarrantyContractPdfResponseSchema,
  accountProviderDataSchemaFor,
  canFinance,
  clampFinanceDuration,
  financeDurationOptions,
  formatPhoneDisplay,
  formatPhoneInput,
  formatProviderFieldValue,
  formatProviderLabel,
  formatTermMiles,
  formatUsd,
  formatVin,
  isRichTextEmpty,
  mergeRateInputs,
  minDownPayment,
  minDownPaymentLabel,
  paginatedListFields,
  paginationQueryFields,
  phoneToE164,
  productDataSchemaFor,
  providerRegistry,
  resolvePricingInputs,
  richTextFromPlain,
  roundCents,
  sortOrderField,
  typedFieldsSchema
};
//# sourceMappingURL=index.js.map